import moment from 'moment';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import { lighten, useTheme } from '@mui/material/styles';
import { Grid, IconButton, Typography } from '@mui/material';
import { noop } from 'lodash';

import VerifiedTag from 'common/modules/VerifiedTag';
import { ProviderVerificationStatus } from 'enums/common.enum';

export const ItemLabelValue = ({
  value,
  label,
  fontSize,
  labelHeight,
  opacity,
  verificationTag,
  isOverrideData,
  boldText,
}) => {
  const theme = useTheme();

  return (
    <Grid container>
      <Grid item sx={{ width: '30%' }}>
        <Typography
          variant={fontSize || 'body2'}
          sx={{
            color: isOverrideData
              ? theme.palette.info.main
              : lighten(theme.palette.text.primary, opacity),
            fontSize: '0.7rem',
            ...(boldText && { fontWeight: 'bold' }),
          }}
        >
          {label || ''}
        </Typography>
      </Grid>
      <Grid
        item
        sx={{
          width: '70%',
          display: 'flex',
          alignItem: 'center',
        }}
      >
        <Typography
          sx={{
            ml: 3,
            minHeight: labelHeight || 18,
            overflow: 'hidden',
            color: isOverrideData
              ? theme.palette.info.main
              : lighten(theme.palette.text.primary, opacity),
            fontSize: '0.7rem',
            ...(!!value && label === 'QS1Patient ID' && { fontWeight: 'bold' }),
            display: 'inline',
            ...(boldText && { fontWeight: 'bold' }),
          }}
          variant={fontSize || 'body2'}
        >
          {value || 'N/A'}
        </Typography>
        {verificationTag && <VerifiedTag status={ProviderVerificationStatus[verificationTag]} />}
      </Grid>
    </Grid>
  );
};

ItemLabelValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  fontSize: PropTypes.string,
  labelHeight: PropTypes.string,
  opacity: PropTypes.number,
  isOverrideData: PropTypes.bool,
  verificationTag: PropTypes.string,
  boldText: PropTypes.bool,
};

ItemLabelValue.defaultProps = {
  label: '',
  value: '',
  fontSize: null,
  labelHeight: null,
  opacity: null,
  isOverrideData: false,
  verificationTag: undefined,
  boldText: false,
};

export const OverrideItemLabelValue = ({
  showEditOption,
  handleModel,
  isHidden,
  labelName,
  labelValue,
  updatedTime,
  pharmacistName,
}) => {
  const theme = useTheme();

  return (
    <Grid container alignItems='flex-start' justifyContent='flex-start'>
      <Grid container direction='row' sx={{ width: '30%' }} justifyContent='space-between'>
        <Grid item sx={{ width: '90%' }}>
          <Typography sx={{ color: theme.palette.info.main, fontSize: '0.7rem' }}>
            {labelName}
          </Typography>
          {!!pharmacistName && (
            <Typography
              variant='caption'
              display='block'
              sx={{ color: theme.palette.info.main, fontSize: '0.65rem' }}
            >
              {pharmacistName === 'System User' ? 'Auto' : pharmacistName}
            </Typography>
          )}
          {!!updatedTime && (
            <Typography
              variant='caption'
              display='block'
              sx={{ color: theme.palette.info.main, fontSize: '0.65rem' }}
            >
              {moment.utc(updatedTime).local().format('MM/DD/YYYY hh:mm:ss A')}
            </Typography>
          )}
        </Grid>

        <Grid item sx={{ width: '10%' }}>
          {showEditOption && (
            <IconButton
              onClick={handleModel}
              hidden={isHidden}
              aria-label={`${labelName} Edit Icon`}
            >
              <EditIcon className='EditIcon' />
            </IconButton>
          )}
        </Grid>
      </Grid>

      <Grid item sx={{ pl: 3, width: '70%' }}>
        <Typography sx={{ color: theme.palette.info.main, fontSize: '0.7rem' }}>
          {labelValue}
        </Typography>
      </Grid>
    </Grid>
  );
};

OverrideItemLabelValue.propTypes = {
  labelValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  labelName: PropTypes.string.isRequired,
  isHidden: PropTypes.bool,
  showEditOption: PropTypes.bool,
  handleModel: PropTypes.func,
  updatedTime: PropTypes.string,
  pharmacistName: PropTypes.string,
};

OverrideItemLabelValue.defaultProps = {
  isHidden: false,
  handleModel: noop,
  showEditOption: false,
  pharmacistName: '',
  updatedTime: '',
};
